import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import StrictModeDroppable from "./StrictModeDroppable"; // Import the custom component
import { db, auth } from "../../firebase";
import { collection, addDoc, doc, getDocs, query, where, updateDoc, deleteDoc } from "firebase/firestore/lite";
import { LanguageContext } from "../../context/LanguageContext";
import lang from "../../components/translations";

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  background: isDragging ? "palegreen" : "",
  ...draggableStyle,
});

// Canonical option values (English) — labels are translated via translations.js
const JOB_TYPE_VALUES = [
  "Full-time",
  "Part-time",
  "Temporary",
  "Internship",
  "Freelance",
  "Seasonal",
  "Volunteer",
  "Per Diem",
  "Rotational",
  "On-call",
];
const RATING_VALUES = ["Excellent", "Very Good", "Good", "Fair", "Poor", "Impossible to Provide"];
const LEAVE_REASON_VALUES = [
  "My Choice",
  "Promoted",
  "Lateral Move",
  "Mutual Decision",
  "Terminated for Cause",
  "Not Leaving",
  "Layoff",
  "Buyout",
  "Other Circumstances",
];

const Experience = ({ nextStep, prevStep, experienceHistory, handleDataChange, experienceRequired = false }) => {
  // Vacancies__c.Experience_Is_Required__c. When the vacancy demands previous
  // experience and the applicant has added none, the confirm modal must not
  // offer the "I do not have any experience" escape hatch — it tells them to
  // add one instead. With at least one job added, or on a vacancy where
  // experience is optional, behaviour is unchanged.
  const mustAddExperience = experienceRequired && experienceHistory.length === 0;
  const { appLanguage } = useContext(LanguageContext);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scrollToForm, setScrollToForm] = useState(false);
  const formRef = React.createRef();
  const [editItemId, setEditItemId] = useState(null);
  const [newEntry, setNewEntry] = useState({
    positionName: "",
    companyName: "",
    companyAddress: "",
    startingSalary: 0,
    endingSalary: 0,
    currency: "USD", // Default currency
    startDate: "",
    endDate: "",
    stillWorking: false,
    fieldOfWork: "",
    typeOfJob: "",
    managerName: "",
    managerTitle: "",
    managerNumber: "",
    arrangeCall: false,
    performanceRating: "",
    mostLiked: "",
    leastLiked: "",
    leaveReason: "",
    leaveExplanation: "",
  });
  const [originalData, setOriginalData] = useState(null); // Store the original data

  useEffect(() => {
    if (!originalData && experienceHistory) {
      setOriginalData({ ...experienceHistory }); // Store the original data on mount
    }
  }, [experienceHistory, originalData]);

  useEffect(() => {
    if (scrollToForm && formRef.current) {
      window.scrollTo({ top: formRef.current.offsetTop, behavior: "smooth" });
      setScrollToForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToForm, showForm]);

  const [isReferenceCallConfirmed, setIsReferenceCallConfirmed] = useState(false);

  useEffect(() => {
    setShowForm(true);
  }, []);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    // Reorder the items in the experience history
    const reorderedItems = reorder(experienceHistory, result.source.index, result.destination.index);

    // Use handleDataChange to update the reordered items in the parent (Wizard) component
    handleDataChange(reorderedItems, "experienceHistory");
  };

  const showToast = (header, message, time) => {
    const toastElement = document.getElementById("liveToast");
    const toastHeader = toastElement.querySelector(".me-auto");
    const toastBody = toastElement.querySelector(".toast-body");
    toastHeader.textContent = header;
    toastBody.textContent = message;
    const toast = new window.bootstrap.Toast(toastElement, {
      delay: time == null ? 5000 : time,
    });
    toast.show();
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();

    // If already submitting, return early to prevent multiple submissions
    if (isSubmitting) return;

    setIsSubmitting(true);

    if (!isReferenceCallConfirmed) {
      setErrors((prev) => ({ ...prev, referenceCall: lang[appLanguage].warnCnfrmRefCall }));
      showToast(lang[appLanguage].warnWaitTitle, lang[appLanguage].warnCnfrmRefCall);
      const box = document.getElementById("referenceCallBox");
      if (box?.scrollIntoView) box.scrollIntoView({ behavior: "smooth", block: "center" });
      setIsSubmitting(false);
      return;
    }

    if (validate()) {
      try {
        let updatedExperienceHistory;
        if (editItemId) {
          // Update existing entry
          updatedExperienceHistory = experienceHistory.map((item) =>
            item.expId === editItemId ? { ...item, ...newEntry } : item
          );
        } else {
          // Add new entry
          updatedExperienceHistory = [...experienceHistory, { ...newEntry, expId: Date.now().toString() }];
        }
        // Pass the updated history back to Wizard
        handleDataChange(updatedExperienceHistory, "experienceHistory");
        showToast(lang[appLanguage].successExpAdded, lang[appLanguage].successMorExpBetr, 20000);
        setShowForm(false);
        setEditItemId(null);
        setNewEntry({
          positionName: "",
          companyName: "",
          companyAddress: "",
          startingSalary: 0,
          endingSalary: 0,
          currency: "USD",
          startDate: "",
          endDate: "",
          stillWorking: false,
          fieldOfWork: "",
          typeOfJob: "",
          managerName: "",
          managerTitle: "",
          managerNumber: "",
          arrangeCall: false,
          performanceRating: "",
          mostLiked: "",
          leastLiked: "",
          leaveReason: "",
          leaveExplanation: "",
        });
        setIsReferenceCallConfirmed(false);
        setShowForm(true);
      } catch (error) {
        console.error("Error adding/updating experience history: ", error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setNewEntry(item);
    setEditItemId(item.expId);
    setShowForm(true);
    setScrollToForm(true);
  };

  const handleDelete = async (expId) => {
    const updatedExperienceHistory = experienceHistory.filter((item) => item.expId !== expId);
    handleDataChange(updatedExperienceHistory, "experienceHistory");
  };

  // Salary fields are shown as currency ("1,500,000") but stored as plain numbers
  const formatSalary = (v) => (v === "" || v == null ? "" : Number(v).toLocaleString("en-US"));

  const handleSalaryChange = (e) => {
    const { name, value } = e.target;
    const digits = value.replace(/[^\d]/g, "").slice(0, 6); // cap at 999999
    setNewEntry({
      ...newEntry,
      [name]: digits === "" ? "" : Number(digits),
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const newValue = type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    // When "still working" is toggled, the leaving fields follow automatically:
    // checked -> reason is "Not Leaving"; unchecked -> candidate must choose again.
    if (name === "stillWorking") {
      setNewEntry({
        ...newEntry,
        stillWorking: newValue,
        leaveReason: newValue ? "Not Leaving" : "",
        leaveExplanation: newValue ? "" : newEntry.leaveExplanation,
      });
      return;
    }

    setNewEntry({
      ...newEntry,
      [name]: newValue,
    });
  };

  const validate = () => {
    let tempErrors = {};
    let valid = true;

    // Validate Company Name
    if (!newEntry.companyName || newEntry.companyName === "") {
      tempErrors.companyName = lang[appLanguage].valBlankField; // Company Name is required
      valid = false;
    }

    // Validate Field of Work
    if (!newEntry.fieldOfWork || newEntry.fieldOfWork === "") {
      tempErrors.fieldOfWork = lang[appLanguage].valBlankField;
      valid = false;
    }

    // Validate Position Name
    if (!newEntry.positionName || newEntry.positionName === "") {
      tempErrors.positionName = lang[appLanguage].valBlankField; // Position Name is required
      valid = false;
    }

    // Validate Start Date: It should not be empty and should not be in the future
    if (!newEntry.startDate || newEntry.startDate === "") {
      tempErrors.startDate = lang[appLanguage].valBlankField; // Start Date is required
      valid = false;
    } else if (new Date(newEntry.startDate) > new Date()) {
      tempErrors.startDate = lang[appLanguage].expValStrtDate; // Start Date cannot be in the future
      valid = false;
    }

    // Validate End Date if "still working" is false
    if (!newEntry.stillWorking) {
      if (!newEntry.endDate || newEntry.endDate === "") {
        tempErrors.endDate = lang[appLanguage].valBlankField; // End Date is required if not still working
        valid = false;
      } else if (new Date(newEntry.endDate) > new Date()) {
        tempErrors.endDate = lang[appLanguage].expValEndDate; // End Date cannot be in the future
        valid = false;
      } else if (new Date(newEntry.startDate) > new Date(newEntry.endDate)) {
        tempErrors.endDate = lang[appLanguage].expValEndAfterStart; // End Date must be after Start Date
        valid = false;
      }
    }

    // Validate Manager Name
    if (!newEntry.managerName || newEntry.managerName === "") {
      tempErrors.managerName = lang[appLanguage].valBlankField; // Manager Name is required
      valid = false;
    }

    // Validate Manager Title
    if (!newEntry.managerTitle || newEntry.managerTitle === "") {
      tempErrors.managerTitle = lang[appLanguage].valBlankField; // Manager Title is required
      valid = false;
    }

    // Validate Performance Rating
    if (!newEntry.performanceRating || newEntry.performanceRating === "") {
      tempErrors.performanceRating = lang[appLanguage].expValPrfRate; // Performance Rating is required
      valid = false;
    }

    // Validate Starting Salary: It should be greater than 0 and within a reasonable range
    if (!newEntry.startingSalary || newEntry.startingSalary === "") {
      tempErrors.startingSalary = lang[appLanguage].valBlankField; // Starting Salary is required
      valid = false;
    } else if (newEntry.startingSalary < 1 || newEntry.startingSalary > 999999) {
      tempErrors.startingSalary = lang[appLanguage].expValStrtSlry; // Salary should be between 1 and 999999
      valid = false;
    }

    // Validate Ending Salary
    if (!newEntry.endingSalary || newEntry.endingSalary === "") {
      tempErrors.endingSalary = lang[appLanguage].valBlankField; // Ending Salary is required
      valid = false;
    }

    // Validate Leave Reason
    if (!newEntry.leaveReason || newEntry.leaveReason === "") {
      tempErrors.leaveReason = lang[appLanguage].expValLevResn; // Leave Reason is required
      valid = false;
    }

    // Validate Leave Explanation (not needed while still working)
    if (!newEntry.stillWorking && (!newEntry.leaveExplanation || newEntry.leaveExplanation === "")) {
      tempErrors.leaveExplanation = lang[appLanguage].expValLevExpln; // Leave Explanation is required
      valid = false;
    }

    // Set the errors state
    setErrors(tempErrors);

    // Return whether the form is valid
    return valid;
  };

  const handleSaveAndContinue = async () => {
    const hasChanged = JSON.stringify(originalData) !== JSON.stringify(experienceHistory);

    if (!hasChanged) {
      nextStep();
      return;
    }

    try {
      const user = auth.currentUser;
      const userEmail = user.email;

      // Query Firebase for the user's personalInfo document
      const personalInfoQuery = query(collection(db, "personalInfo"), where("email", "==", userEmail));
      const querySnapshot = await getDocs(personalInfoQuery);

      if (!querySnapshot.empty) {
        const personalInfoDoc = querySnapshot.docs[0];

        // Fetch current experience history from Firebase to compare with local state
        const experienceSnapshot = await getDocs(
          collection(db, "personalInfo", personalInfoDoc.id, "experienceHistory")
        );
        const firebaseExperienceIds = experienceSnapshot.docs.map((doc) => doc.id); // Get all current Firebase document IDs

        // Track local document IDs
        const localExperienceIds = experienceHistory.map((entry) => entry.firebaseDocId).filter(Boolean);

        // Delete any documents in Firebase that are not in the local experienceHistory array
        for (const firebaseDocId of firebaseExperienceIds) {
          if (!localExperienceIds.includes(firebaseDocId)) {
            const docRef = doc(db, "personalInfo", personalInfoDoc.id, "experienceHistory", firebaseDocId);
            await deleteDoc(docRef); // Delete from Firebase
          }
        }

        // Now, update or add documents to Firebase
        for (const entry of experienceHistory) {
          if (entry.firebaseDocId) {
            // If the document ID exists locally, update the document in Firestore
            const experienceDocRef = doc(
              db,
              "personalInfo",
              personalInfoDoc.id,
              "experienceHistory",
              entry.firebaseDocId
            );
            await updateDoc(experienceDocRef, {
              positionName: entry.positionName,
              companyName: entry.companyName,
              companyAddress: entry.companyAddress,
              startingSalary: entry.startingSalary,
              endingSalary: entry.endingSalary,
              currency: entry.currency,
              startDate: entry.startDate,
              endDate: entry.endDate,
              stillWorking: entry.stillWorking,
              fieldOfWork: entry.fieldOfWork,
              typeOfJob: entry.typeOfJob,
              managerName: entry.managerName,
              managerTitle: entry.managerTitle,
              managerNumber: entry.managerNumber,
              arrangeCall: entry.arrangeCall,
              performanceRating: entry.performanceRating,
              mostLiked: entry.mostLiked,
              leastLiked: entry.leastLiked,
              leaveReason: entry.leaveReason,
              leaveExplanation: entry.leaveExplanation,
              // Do NOT store firebaseDocId in Firestore
            });
          } else {
            // If there's no firebaseDocId, add a new document
            const newDocRef = await addDoc(collection(db, "personalInfo", personalInfoDoc.id, "experienceHistory"), {
              positionName: entry.positionName,
              companyName: entry.companyName,
              companyAddress: entry.companyAddress,
              startingSalary: entry.startingSalary,
              endingSalary: entry.endingSalary,
              currency: entry.currency,
              startDate: entry.startDate,
              endDate: entry.endDate,
              stillWorking: entry.stillWorking,
              fieldOfWork: entry.fieldOfWork,
              typeOfJob: entry.typeOfJob,
              managerName: entry.managerName,
              managerTitle: entry.managerTitle,
              managerNumber: entry.managerNumber,
              arrangeCall: entry.arrangeCall,
              performanceRating: entry.performanceRating,
              mostLiked: entry.mostLiked,
              leastLiked: entry.leastLiked,
              leaveReason: entry.leaveReason,
              leaveExplanation: entry.leaveExplanation,
              // Do NOT store firebaseDocId in Firestore
            });

            // Store the generated Firestore document ID locally for future updates/deletions
            entry.firebaseDocId = newDocRef.id;
          }
        }

        setLoading(false);

        // After saving to Firebase, move to the next step
        nextStep();
      } else {
        console.error("No personalInfo document found for the user");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error saving experience data to Firebase before continuing:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const forms = document.querySelectorAll(".needs-validation");
    Array.from(forms).forEach((form) => {
      form.addEventListener(
        "submit",
        (event) => {
          if (!form.checkValidity()) {
            event.preventDefault();
          }
          form.classList.add("was-validated");
        },
        false
      );
    });
  }, []);

  // Red asterisk for required fields + section heading helper
  const req = <span className="text-danger ms-1">*</span>;
  const SectionTitle = ({ icon, text }) => (
    <h5 className="mt-2 mb-3 pb-2 border-bottom text-success">
      <i className={`bi ${icon} me-2`}></i>
      {text}
    </h5>
  );

  // ----- Saved-card display helpers -----
  const fmtMonthYear = (d) => {
    if (!d) return "";
    const date = new Date(d);
    if (isNaN(date)) return d;
    return date.toLocaleDateString(appLanguage === "ar" ? "ar" : "en-US", { month: "short", year: "numeric" });
  };

  const fmtDuration = (start, end, stillWorking) => {
    if (!start) return "";
    const s = new Date(start);
    const e = stillWorking ? new Date() : end ? new Date(end) : null;
    if (!e || isNaN(s) || isNaN(e)) return "";
    let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    if (months < 0) return "";
    const y = Math.floor(months / 12);
    const m = months % 12;
    const parts = [];
    if (y > 0) parts.push(`${y} ${lang[appLanguage].expDurYears}`);
    if (m > 0 || y === 0) parts.push(`${m} ${lang[appLanguage].expDurMonths}`);
    return parts.join(" ");
  };

  const fmtMoney = (v) => (v === "" || v == null ? "" : `$${Number(v).toLocaleString("en-US")}`);

  // One labeled value in the saved-card detail grid (hidden when empty)
  const Detail = ({ icon, label, value, wide }) =>
    value == null || value === "" ? null : (
      <div className={wide ? "col-12" : "col-12 col-md-6"}>
        <div className="d-flex align-items-start px-2 py-2">
          <i className={`bi ${icon} text-success mt-1 me-2`}></i>
          <div>
            <div className="small text-muted">{label}</div>
            <div className="fw-medium">{String(value)}</div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="container">
      <p className="text-muted">{lang[appLanguage].expDisc1}</p>
      <p className="text-muted">{lang[appLanguage].expDisc2}</p>

      <p className="fw-medium text-muted">{lang[appLanguage].expDisc3}</p>

      <p className="fs-5 fw-medium text-muted text-center">{lang[appLanguage].expDisc4}</p>
      <hr />
      <div className="mb-4 mx-0 navbar rounded-2 row shadow">
        <div className="col-12 col-md-6">
          <h4>
            <i className="bi bi-briefcase-fill"></i> {lang[appLanguage].expTitle}
          </h4>
        </div>
        <div className="col-12 col-md-6">
          <button
            type="button"
            className="btn btn-light float-end"
            onClick={() => setShowForm(true)}
            disabled={showForm}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              className="bi bi-plus-circle me-2"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
            </svg>
            {lang[appLanguage].btnAddExpHitry}
          </button>
        </div>
      </div>

      {showForm && (
        <div ref={formRef} className="row col-12 fade-in">
          <div className="col">
            <div className="card m-3 mx-auto rounded-3 shadow">
              <div className="card-body">
                <form onSubmit={handleAddOrUpdate} className="needs-validation" noValidate>
                  <p className="small text-muted">{lang[appLanguage].expReqLegend}</p>
                  <SectionTitle icon="bi-building" text={lang[appLanguage].expSecCompany} />
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expCpnyName}
                          {req}
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          className={`form-control ${errors.companyName ? "is-invalid" : ""}`}
                          value={newEntry.companyName}
                          required
                          onChange={handleChange}
                          maxLength={200}
                        />
                        <div className="invalid-feedback">{errors.companyName}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">{lang[appLanguage].expCpnyAdrs}</label>
                        <input
                          type="text"
                          name="companyAddress"
                          className={`form-control ${errors.companyAddress ? "is-invalid" : ""}`}
                          value={newEntry.companyAddress}
                          onChange={handleChange}
                          maxLength={255}
                        />
                        <div className="invalid-feedback">{errors.companyAddress}</div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expWorkField}
                          {req}
                        </label>
                        <input
                          type="text"
                          name="fieldOfWork"
                          placeholder="Ex: Medical"
                          className={`form-control ${errors.fieldOfWork ? "is-invalid" : ""}`}
                          value={newEntry.fieldOfWork}
                          onChange={handleChange}
                          maxLength={200}
                        />
                        <div className="invalid-feedback">{errors.fieldOfWork}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expPosTitle}
                          {req}
                        </label>
                        <input
                          type="text"
                          name="positionName"
                          placeholder="Ex: Medical Representative"
                          className={`form-control ${errors.positionName ? "is-invalid" : ""}`}
                          value={newEntry.positionName}
                          required
                          maxLength={200}
                          onChange={handleChange}
                        />
                        <div className="invalid-feedback">{errors.positionName}</div>
                      </div>
                    </div>
                  </div>
                  <SectionTitle icon="bi-calendar-range" text={lang[appLanguage].expSecDuration} />
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expStrtDate}
                          {req}
                        </label>
                        <input
                          name="startDate"
                          type="date"
                          max={new Date().toISOString().split("T")[0]}
                          className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                          value={newEntry.startDate}
                          required
                          onChange={handleChange}
                        />
                        <div className="invalid-feedback">{errors.startDate}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expEndDate}
                          {!newEntry.stillWorking && req}
                        </label>
                        <input
                          type="date"
                          name="endDate"
                          className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
                          value={newEntry.stillWorking ? "" : newEntry.endDate}
                          onChange={handleChange}
                          required={!newEntry.stillWorking}
                          disabled={newEntry.stillWorking} // Disable the input if still working
                          min={
                            newEntry.startDate
                              ? new Date(new Date(newEntry.startDate).getTime() + 86400000).toISOString().split("T")[0]
                              : ""
                          }
                          max={new Date().toISOString().split("T")[0]}
                        />
                        <div className="invalid-feedback">{errors.endDate}</div>
                        <div className="form-check mt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="stillWorking"
                            checked={newEntry.stillWorking}
                            onChange={handleChange}
                            id="stillWorking"
                          />
                          <label className="form-check-label" htmlFor="stillWorking">
                            {lang[appLanguage].expCrntlyWork}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expWorkType}
                          {req}
                        </label>
                        <select
                          className={`form-select ${errors.typeOfJob ? "is-invalid" : ""}`}
                          value={newEntry.typeOfJob}
                          name="typeOfJob"
                          onChange={handleChange}
                          required
                        >
                          <option value="" disabled>
                            {lang[appLanguage].selectOpt}
                          </option>
                          {JOB_TYPE_VALUES.map((v) => (
                            <option key={v} value={v}>
                              {lang[appLanguage].expJobTypes[v] || v}
                            </option>
                          ))}
                        </select>
                        <div className="invalid-feedback">{errors.typeOfJob}</div>
                      </div>
                    </div>
                  </div>
                  <SectionTitle icon="bi-cash-coin" text={lang[appLanguage].expSecSalary} />
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expSlryStrt}
                          {req}
                        </label>
                        <div className="input-group mb-3" dir="ltr">
                          <span className="input-group-text">$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            className={`form-control ${errors.startingSalary ? "is-invalid" : ""}`}
                            value={formatSalary(newEntry.startingSalary)}
                            onChange={handleSalaryChange}
                            name="startingSalary"
                            placeholder="1,500"
                            required
                          />
                          <span className="input-group-text text-muted">USD</span>
                          <div className="invalid-feedback">{errors.startingSalary}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expSlryEnd}
                          {req}
                        </label>
                        <div className="input-group mb-3" dir="ltr">
                          <span className="input-group-text">$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            className={`form-control ${errors.endingSalary ? "is-invalid" : ""}`}
                            value={formatSalary(newEntry.endingSalary)}
                            onChange={handleSalaryChange}
                            name="endingSalary"
                            placeholder="2,000"
                            required
                          />
                          <span className="input-group-text text-muted">USD</span>
                          <div className="invalid-feedback">{errors.endingSalary}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <SectionTitle icon="bi-person-badge" text={lang[appLanguage].expSecManager} />
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expMngrName}
                          {req}
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors.managerName ? "is-invalid" : ""}`}
                          value={newEntry.managerName}
                          onChange={handleChange}
                          required
                          maxLength={200}
                          name="managerName"
                        />
                        <div className="invalid-feedback">{errors.managerName}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expMngrTitle}
                          {req}
                        </label>
                        <input
                          type="text"
                          required
                          className={`form-control ${errors.managerTitle ? "is-invalid" : ""}`}
                          value={newEntry.managerTitle}
                          onChange={handleChange}
                          name="managerTitle"
                          maxLength={200}
                        />
                        <div className="invalid-feedback">{errors.managerTitle}</div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expMngrRate}
                          {req}
                        </label>
                        <select
                          className={`form-select ${errors.performanceRating ? "is-invalid" : ""}`}
                          value={newEntry.performanceRating}
                          onChange={handleChange}
                          required
                          name="performanceRating"
                        >
                          <option value="" disabled>
                            {lang[appLanguage].selectOpt}
                          </option>
                          {RATING_VALUES.map((v) => (
                            <option key={v} value={v}>
                              {lang[appLanguage].expRatings[v] || v}
                            </option>
                          ))}
                        </select>
                        <div className="invalid-feedback">{errors.performanceRating}</div>
                      </div>
                    </div>
                  </div>
                  <div
                    id="referenceCallBox"
                    className={`card mb-3 ${errors.referenceCall ? "border-danger" : "border-warning"}`}
                  >
                    <div className="card-body py-3">
                      <h6 className="fw-semibold">
                        <i
                          className={`bi bi-telephone-outbound me-2 ${errors.referenceCall ? "text-danger" : "text-warning"}`}
                        ></i>
                        {lang[appLanguage].expRefCalTitle}
                        {req}
                      </h6>
                      <p className="small text-muted mb-2">{lang[appLanguage].expNoteRefCal}</p>
                      <div className="form-check">
                        <input
                          className={`form-check-input ${errors.referenceCall ? "is-invalid" : ""}`}
                          type="checkbox"
                          id="referenceCallConfirm"
                          name="referenceCallConfirm"
                          checked={isReferenceCallConfirmed}
                          onChange={(e) => {
                            setIsReferenceCallConfirmed(e.target.checked);
                            if (e.target.checked) {
                              setErrors((prev) => ({ ...prev, referenceCall: undefined }));
                            }
                          }}
                        />
                        <label className="form-check-label fw-medium" htmlFor="referenceCallConfirm">
                          {lang[appLanguage].expRefCalAction}
                        </label>
                        {errors.referenceCall && (
                          <div className="invalid-feedback d-block">{errors.referenceCall}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <SectionTitle icon="bi-box-arrow-right" text={lang[appLanguage].expSecLeaving} />
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">{lang[appLanguage].expMostLike}</label>
                        <textarea
                          className={`form-control ${errors.mostLiked ? "is-invalid" : ""}`}
                          value={newEntry.mostLiked}
                          onChange={handleChange}
                          maxLength={255}
                          name="mostLiked"
                        ></textarea>
                        <div className="invalid-feedback">{errors.mostLiked}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">{lang[appLanguage].expLstEnjy}</label>
                        <textarea
                          className={`form-control ${errors.leastLiked ? "is-invalid" : ""}`}
                          value={newEntry.leastLiked}
                          name="leastLiked"
                          maxLength={255}
                          onChange={handleChange}
                        ></textarea>
                        <div className="invalid-feedback">{errors.leastLiked}</div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {lang[appLanguage].expLevRsn}
                          {req}
                        </label>
                        <select
                          className={`form-select ${errors.leaveReason ? "is-invalid" : ""}`}
                          value={newEntry.leaveReason}
                          onChange={handleChange}
                          name="leaveReason"
                          required
                          disabled={newEntry.stillWorking}
                        >
                          <option value="" disabled>
                            {lang[appLanguage].selectOpt}
                          </option>
                          {LEAVE_REASON_VALUES.map((v) => (
                            <option key={v} value={v}>
                              {lang[appLanguage].expLeaveReasons[v] || v}
                            </option>
                          ))}
                        </select>
                        <div className="invalid-feedback">{errors.leaveReason}</div>
                      </div>
                    </div>
                  </div>
                  {!newEntry.stillWorking && (
                    <div className="row">
                      <div className="col">
                        <div className="mb-3">
                          <label className="form-label">
                            {lang[appLanguage].expLevExpln}
                            {req}
                          </label>
                          <textarea
                            className={`form-control ${errors.leaveExplanation ? "is-invalid" : ""}`}
                            value={newEntry.leaveExplanation}
                            onChange={handleChange}
                            name="leaveExplanation"
                            maxLength={255}
                            required
                          ></textarea>
                          <div className="invalid-feedback">{errors.leaveExplanation}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col">
                      <button type="submit" className="btn btn-primary">
                        {isSubmitting ? (
                          <div className="spinner-border text-light" role="status">
                            <span className="px-1 status"> {lang[appLanguage].loading}</span>
                          </div>
                        ) : editItemId ? (
                          lang[appLanguage].btnUpdate
                        ) : (
                          lang[appLanguage].btnAdd
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <form>
        <div className="row mt-2">
          <DragDropContext onDragEnd={onDragEnd}>
            <StrictModeDroppable droppableId="experienceHistory">
              {(provided, snapshot) => (
                <div id="accordionList" className="col accordion" {...provided.droppableProps} ref={provided.innerRef}>
                  {experienceHistory.map((item, index) => (
                    <Draggable key={item.expId} draggableId={item.expId} index={index}>
                      {(provided, snapshot) => (
                        <div
                          className="accordion-item"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                        >
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${item.expId}`}
                            aria-expanded="false"
                            aria-controls={`collapse${item.expId}`}
                          >
                            <div className="d-flex align-items-center w-100 me-2">
                              <div
                                className="bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{ width: 44, height: 44, flexShrink: 0 }}
                              >
                                <i className="bi bi-briefcase-fill"></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="fw-semibold">{item.positionName}</div>
                                <div className="small text-body-secondary">
                                  <i className="bi bi-building me-1"></i>
                                  {item.companyName}
                                  {item.startDate && (
                                    <>
                                      {" · "}
                                      <i className="bi bi-calendar3 me-1"></i>
                                      {fmtMonthYear(item.startDate)} –{" "}
                                      {item.stillWorking
                                        ? lang[appLanguage].expBadgeCurrent
                                        : fmtMonthYear(item.endDate)}
                                      {fmtDuration(item.startDate, item.endDate, item.stillWorking) && (
                                        <> · {fmtDuration(item.startDate, item.endDate, item.stillWorking)}</>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="d-none d-md-block text-end" style={{ flexShrink: 0 }}>
                                {item.typeOfJob && (
                                  <span className="badge rounded-pill text-bg-light border me-1">
                                    {lang[appLanguage].expJobTypes[item.typeOfJob] || item.typeOfJob}
                                  </span>
                                )}
                                {item.stillWorking && (
                                  <span className="badge rounded-pill text-bg-success">
                                    {lang[appLanguage].expBadgeCurrent}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                          <div
                            id={`collapse${item.expId}`}
                            className="accordion-collapse collapse"
                            data-bs-parent="#accordionList"
                          >
                            <div className="accordion-body">
                              <div className="d-flex justify-content-end gap-2 mb-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() => handleEdit(item)}
                                >
                                  <i className="bi bi-pencil me-1"></i>
                                  {lang[appLanguage].expBtnEdit}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(item.expId)}
                                >
                                  <i className="bi bi-trash3 me-1"></i>
                                  {lang[appLanguage].expBtnDelete}
                                </button>
                              </div>
                              <div className="row g-0 border rounded-3 bg-light bg-opacity-50 mb-3">
                                <Detail icon="bi-briefcase" label={lang[appLanguage].expPosTitle} value={item.positionName} />
                                <Detail icon="bi-building" label={lang[appLanguage].expCpnyName} value={item.companyName} />
                                <Detail icon="bi-diagram-3" label={lang[appLanguage].expWorkField} value={item.fieldOfWork} />
                                <Detail icon="bi-geo-alt" label={lang[appLanguage].expCpnyAdrs} value={item.companyAddress} />
                                <Detail
                                  icon="bi-calendar-event"
                                  label={lang[appLanguage].expStrtDate}
                                  value={fmtMonthYear(item.startDate)}
                                />
                                <Detail
                                  icon="bi-calendar-check"
                                  label={lang[appLanguage].expEndDate}
                                  value={
                                    item.stillWorking ? lang[appLanguage].expBadgeCurrent : fmtMonthYear(item.endDate)
                                  }
                                />
                                <Detail
                                  icon="bi-clock-history"
                                  label={lang[appLanguage].expDuration}
                                  value={fmtDuration(item.startDate, item.endDate, item.stillWorking)}
                                />
                                <Detail
                                  icon="bi-person-workspace"
                                  label={lang[appLanguage].expWorkType}
                                  value={lang[appLanguage].expJobTypes[item.typeOfJob] || item.typeOfJob}
                                />
                                <Detail
                                  icon="bi-cash"
                                  label={lang[appLanguage].expSlryStrt}
                                  value={fmtMoney(item.startingSalary)}
                                />
                                <Detail
                                  icon="bi-cash-stack"
                                  label={lang[appLanguage].expSlryEnd}
                                  value={fmtMoney(item.endingSalary)}
                                />
                                <Detail icon="bi-person" label={lang[appLanguage].expMngrName} value={item.managerName} />
                                <Detail
                                  icon="bi-person-badge"
                                  label={lang[appLanguage].expMngrTitle}
                                  value={item.managerTitle}
                                />
                                <Detail
                                  icon="bi-star"
                                  label={lang[appLanguage].expMngrRate}
                                  value={lang[appLanguage].expRatings[item.performanceRating] || item.performanceRating}
                                />
                                <Detail
                                  icon="bi-box-arrow-right"
                                  label={lang[appLanguage].expLevRsn}
                                  value={lang[appLanguage].expLeaveReasons[item.leaveReason] || item.leaveReason}
                                />
                                <Detail
                                  wide
                                  icon="bi-chat-left-text"
                                  label={lang[appLanguage].expLevExpln}
                                  value={item.leaveExplanation}
                                />
                                <Detail
                                  wide
                                  icon="bi-hand-thumbs-up"
                                  label={lang[appLanguage].expMostLike}
                                  value={item.mostLiked}
                                />
                                <Detail
                                  wide
                                  icon="bi-hand-thumbs-down"
                                  label={lang[appLanguage].expLstEnjy}
                                  value={item.leastLiked}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        </div>
        <div className="row">
          <div className="col-12 col-md-6 mt-2">
            <button type="button" className="btn btn-success button me-1" onClick={prevStep}>
              {lang[appLanguage].btnBack}
            </button>
            <button
              type="button"
              className="btn btn-success button"
              data-bs-toggle="modal"
              data-bs-target="#staticBackdrop"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                  <span className="px-1 status" role="status">
                    {lang[appLanguage].loading}
                  </span>
                </>
              ) : (
                lang[appLanguage].btnSaveCont
              )}
            </button>
          </div>
        </div>
      </form>
      <div
        className="modal fade"
        id="staticBackdrop"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-labelledby="staticBackdropLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="staticBackdropLabel">
                {mustAddExperience ? lang[appLanguage].expReqModlTitle : lang[appLanguage].expModlTitle}
              </h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body fw-semibold">
              {mustAddExperience ? (
                <>
                  <p>{lang[appLanguage].expReqModlDisc1}</p>
                  <p>{lang[appLanguage].expReqModlDisc2}</p>
                </>
              ) : (
                <>
                  <p>{lang[appLanguage].expModlDisc1}</p>
                  <p>{lang[appLanguage].expModlDisc2}</p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                {lang[appLanguage].btnClose}
              </button>
              {!mustAddExperience && (
                <button
                  type="button"
                  onClick={() => {
                    setLoading(true);
                    handleSaveAndContinue();
                  }}
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                      <span className="px-1 status" role="status">
                        {lang[appLanguage].loading}
                      </span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check2-circle me-2"></i>
                      {experienceHistory.length > 0
                        ? appLanguage === "en"
                          ? `I'm done — I only had ${experienceHistory.length} ${experienceHistory.length === 1 ? "Job" : "Jobs"}`
                          : `لقد انتهيت — كان لدي ${experienceHistory.length} ${experienceHistory.length === 1 ? "وظيفة واحدة" : "وظائف"} فقط`
                        : appLanguage === "en"
                          ? "I do not have any experience"
                          : "ليس لدي أي خبرة"}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Experience.propTypes = {
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  experienceHistory: PropTypes.array.isRequired,
  handleDataChange: PropTypes.func.isRequired,
  experienceRequired: PropTypes.bool,
};

export default Experience;
